<!-- Nested under a Shipment collapsable in the RWO dashboard view, displays a breakdown of the shipment's containers -->
<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import StatusBadge from '@/components/common/StatusBadge.vue'
import { useContainers } from '@/composables/rc/containers'
import type { IWorkOrderDashboardShipment, IWorkOrderDashboardShipmentContainer } from '@/types/rc/work-orders'
import { Icon } from '@iconify/vue'
import type { TableColumnCtx } from 'element-plus'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const checkboxColWidth = 55

const emit = defineEmits(['containerDetails', 'addContainers', 'assignContainers', 'editContainer'])

// Selection state
const containersSelected = ref<IWorkOrderDashboardShipmentContainer[]>()

const isUnassigned = computed(() => props.shipment.id === 'Unassigned')
const anySelected = computed(() => !!containersSelected.value?.length)

const props = withDefaults(
  defineProps<{
    shipment: IWorkOrderDashboardShipment
    expanded: boolean
    editable?: boolean
  }>(),
  {
    editable: true,
  }
)

const { unassignContainerFromShipmentMutation, deleteContainerMutation } = useContainers()

const onUnassignContainer = async (container: IWorkOrderDashboardShipmentContainer) => {
  unassignContainerFromShipmentMutation.mutate({
    shipmentId: props.shipment.id,
    containerID: container.id,
  })
}

const onDeleteContainer = async (container: IWorkOrderDashboardShipmentContainer) => {
  DeleteConfirmationModal(
    'Delete Container',
    `Are you sure you want to delete this container? This action cannot be undone`,
    container.id,
    deleteContainerMutation.mutateAsync
  )
}

const setContainersSelected = (rows: IWorkOrderDashboardShipmentContainer[]) => {
  containersSelected.value = rows
}

const onRowClick = (
  row: IWorkOrderDashboardShipmentContainer,
  column: TableColumnCtx<IWorkOrderDashboardShipmentContainer> | null
) => {
  if (props.editable && column?.type !== 'selection') emit('containerDetails', row.id)
}

const navigateToShipmentDetails = (shipmentID: string) => {
  router.push({ name: 'rc-logistics-shipment-details', params: { shipmentId: shipmentID } })
}
</script>

<template>
  <el-card shadow="never">
    <el-collapse-item :name="shipment.id">
      <!-- Header -->
      <template #title>
        <header class="inline-flex w-[480px] grow-1">
          <div class="w-full inline-flex items-center gap-spacing-md justify-start">
            <!-- Id -->
            <h4
              v-if="!isUnassigned"
              class="text-secondary text-sm text-left whitespace-nowrap w-[120px] shrink-0 grow-0"
            >
              <span class="text-tertiary">Shipment</span>
              #{{ shipment.id }}
            </h4>
            <h4 v-else class="text-tertiary text-sm text-left w-[120px] shrink-0 grow-0">Unassigned</h4>

            <!-- # Container -->
            <h4 class="text-secondary text-sm text-left w-[120px]">
              {{ shipment.total_containers }}
              <span class="text-tertiary text-sm">
                &nbsp; {{ shipment.total_containers == 1 ? 'container' : 'containers' }}
              </span>
            </h4>

            <!-- # Miners -->
            <h4 class="text-secondary text-left text-sm whitespace-nowrap">
              {{ shipment.total_miners }}
              <span class="text-tertiary text-sm">&nbsp; {{ shipment.total_miners == 1 ? 'miner' : 'miners' }}</span>
            </h4>

            <!-- Status -->
            <div class="ml-auto">
              <status-badge v-if="shipment.status" :status="shipment.status" />
            </div>

            <!-- Btns -->
            <div v-if="editable">
              <el-button v-if="shipment.id != 'Unassigned'" link @click="navigateToShipmentDetails(shipment.id)">
                <Icon icon="material-symbols:open-in-new" :height="16" :inline="true" />
              </el-button>
            </div>
          </div>
        </header>
      </template>

      <div class="flex flex-col gap-spacing-md my-spacing-md">
        <hr class="border-component-stroke-primary" />

        <el-button
          v-if="anySelected"
          class="w-max bg-fill-primary"
          @click="emit('assignContainers', containersSelected, shipment.type)"
        >
          Assign to Shipment
        </el-button>

        <!-- Containers table -->
        <el-table
          :data="shipment.containers"
          :show-header="false"
          empty-text="No containers"
          class="text-tertiary"
          :max-height="864"
          width="100%"
          @selection-change="setContainersSelected"
          @row-click="onRowClick"
        >
          <!-- Selection checkboxes go only for unassigned containers -->
          <el-table-column v-if="editable && isUnassigned" type="selection" :width="checkboxColWidth" />
          <!-- Ajust width to keep alignment of the rest of the columns when there's selection checkboxes -->
          <el-table-column prop="number" />
          <el-table-column prop="type" :width="70" />
          <el-table-column>
            <template #default="{ row }">
              <span class="text-secondary font-medium">{{ row.total_miners }}</span>
              <span>&nbsp;miners</span>
            </template>
          </el-table-column>
          <!-- Leave these widths here undefined so they can grow/autoadjust -->
          <el-table-column>
            <template #default="{ row }">
              <StatusBadge :status="row.status" />
            </template>
          </el-table-column>
          <!-- Push these one right: -->
          <el-table-column v-if="editable && !isUnassigned" fixed="right" :width="checkboxColWidth * 1.5">
            <template #default="{ row }">
              <div class="flex gap-2">
                <el-button link title="Edit" @click.stop="emit('editContainer', row)">
                  <Icon icon="material-symbols:edit-outline" :height="16" />
                </el-button>
                <el-button
                  title="Unassign"
                  link
                  @click.stop="onUnassignContainer(row as IWorkOrderDashboardShipmentContainer)"
                >
                  <icon icon="mdi:close" />
                </el-button>
                <el-button
                  title="Delete"
                  link
                  @click.stop="onDeleteContainer(row as IWorkOrderDashboardShipmentContainer)"
                >
                  <icon icon="mdi:trash-can-outline" />
                </el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column v-else-if="editable" fixed="right" :width="checkboxColWidth">
            <template #default="{ row }">
              <div class="flex gap-2">
                <el-button link title="Edit" @click.stop="emit('editContainer', row)">
                  <Icon icon="material-symbols:edit-outline" :height="16" />
                </el-button>
                <el-button
                  title="Delete"
                  link
                  @click.stop="onDeleteContainer(row as IWorkOrderDashboardShipmentContainer)"
                >
                  <icon icon="mdi:trash-can-outline" />
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- Bottom link -->
        <el-link
          v-if="editable"
          class="text-text-brand w-max"
          @click="emit('addContainers', shipment.id, shipment.type)"
          >+ Add containers</el-link
        >
      </div>
    </el-collapse-item>
  </el-card>
</template>

<style scoped lang="scss">
/* Override Element Plus styles */
:deep() {
  .el_card__body {
    padding: 16px;
    max-width: 568px;
  }

  .el-collapse-item__header {
    border: none;
    display: flex;
    flex-direction: row-reverse;
    height: 32px;
    margin: 0;
  }

  .el-table tr {
    background: transparent;
  }

  .el-collapse-item__wrap {
    border-bottom: 0;
  }

  .el-table__row {
    border-bottom: 0;
    cursor: pointer;
  }
}
</style>
